import React, { useState, useCallback, useEffect } from 'react';
import { assembleMonthlyPack } from '../parsers/monthlyMIS';
import { loadMISData, saveMISData } from '../supabase';

// ─── UTILITIES ───────────────────────────────────────────────────────────────
function fmt(v, d = 0) {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}

// Fixed FX rates — pegged currencies, no need to change
const FX = { AED: 3.675, SAR: 3.75 }; // units per USD
function toUSD(v, fromCurrency) { return (parseFloat(v) || 0) / (FX[fromCurrency] || 1); }

function currencyLabel(entity) {
  if (entity === 'saudi') return 'SAR';
  if (entity === 'group') return 'USD';
  return 'AED';
}
function fmtCurrency(v, entity) { return currencyLabel(entity) + ' ' + fmt(v); }
function fmtAED(v) { return 'AED ' + fmt(v); }

// ─── GROUP CONSOLIDATION ─────────────────────────────────────────────────────
// Converts UAE (AED) + Saudi (SAR) P&L lines to USD and sums them.
// Returns a pack shaped identically to a normal entity pack so all
// existing sub-tab components work without modification.
function buildGroupPack(uaePack, saudiPack) {
  if (!uaePack && !saudiPack) return null;

  // Helper: convert a MIS sheet (Consol/Dubai/Warehouse/Haziq/Shahalam) to USD
  function convertSheet(sheet, currency) {
    if (!sheet) return null;
    return {
      ...sheet,
      lines: sheet.lines.map(line => ({
        ...line,
        values: line.values.map(v => toUSD(v, currency)),
        ytd: toUSD(line.ytd, currency),
      })),
    };
  }

  // Helper: sum two converted sheets line by line (matched by label)
  function sumSheets(sheetA, sheetB) {
    if (!sheetA && !sheetB) return null;
    if (!sheetA) return sheetB;
    if (!sheetB) return sheetA;
    const months = sheetA.months || sheetB.months;
    const allLabels = [...new Set([
      ...sheetA.lines.map(l => l.label),
      ...sheetB.lines.map(l => l.label),
    ])];
    const lines = allLabels.map(label => {
      const a = sheetA.lines.find(l => l.label === label);
      const b = sheetB.lines.find(l => l.label === label);
      const len = months.length;
      return {
        label,
        values: Array.from({ length: len }, (_, i) =>
          (a?.values[i] || 0) + (b?.values[i] || 0)
        ),
        ytd: (a?.ytd || 0) + (b?.ytd || 0),
      };
    });
    return { months, lines };
  }

  // Helper: convert and sum a full mis_ytd or mis_period object
  function consolidateMIS(uaeMIS, saudiMIS) {
    if (!uaeMIS && !saudiMIS) return null;
    const uaeConsol = convertSheet(uaeMIS?.Consol, 'AED');
    const sarConsol = convertSheet(saudiMIS?.Consol, 'SAR');
    const Consol = sumSheets(uaeConsol, sarConsol);

    // Dubai/Warehouse (UAE) + Haziq/Shahalam (Saudi) — keep as separate named sheets
    const Dubai = convertSheet(uaeMIS?.Dubai, 'AED');
    const Warehouse = convertSheet(uaeMIS?.Warehouse, 'AED');
    const Haziq = convertSheet(saudiMIS?.Haziq, 'SAR');
    const Shahalam = convertSheet(saudiMIS?.Shahalam, 'SAR');

    return { Consol, Dubai, Warehouse, Haziq, Shahalam };
  }

  // Helper: convert and merge AR/AP rows
  function consolidateAgeing(uaeRows, saudiRows, saudiCurrency) {
    if (!uaeRows && !saudiRows) return null;
    const fields = ['pending', 'lt60', 'd60_90', 'd90_180', 'd180_365', 'gt365'];
    const uaeConverted = (uaeRows || []).map(r => ({
      ...r,
      ...Object.fromEntries(fields.map(f => [f, toUSD(r[f] || 0, 'AED')])),
    }));
    const sarConverted = (saudiRows || []).map(r => ({
      ...r,
      client: r.client + ' (SA)',
      ...Object.fromEntries(fields.map(f => [f, toUSD(r[f] || 0, saudiCurrency)])),
    }));
    return [...uaeConverted, ...sarConverted];
  }

  // Helper: convert customer GP rows
  function consolidateGP(uaeGP, saudiGP) {
    if (!uaeGP && !saudiGP) return null;
    const months = uaeGP?.months || saudiGP?.months || [];
    function convertCustomers(customers, currency) {
      return (customers || []).map(c => ({
        ...c,
        ytdSales: toUSD(c.ytdSales, currency),
        ytdGP: toUSD(c.ytdGP, currency),
        monthly: (c.monthly || []).map(m => ({
          ...m,
          sales: toUSD(m.sales, currency),
          gp: toUSD(m.gp, currency),
        })),
      }));
    }
    const uaeFreight = convertCustomers(uaeGP?.freight, 'AED');
    const sarFreight = convertCustomers(saudiGP?.freight, 'SAR').map(c => ({ ...c, name: c.name + ' (SA)' }));
    const uaeWarehouse = convertCustomers(uaeGP?.warehouse, 'AED');
    const sarWarehouse = convertCustomers(saudiGP?.warehouse, 'SAR').map(c => ({ ...c, name: c.name + ' (SA)' }));
    return {
      months,
      freight: [...uaeFreight, ...sarFreight],
      warehouse: [...uaeWarehouse, ...sarWarehouse],
    };
  }

  return {
    _group: true,
    mis_ytd: consolidateMIS(uaePack?.mis_ytd, saudiPack?.mis_ytd),
    mis_period: consolidateMIS(uaePack?.mis_period, saudiPack?.mis_period),
    gp: consolidateGP(uaePack?.gp, saudiPack?.gp),
    ar_data: consolidateAgeing(uaePack?.ar_data, saudiPack?.ar_data, 'SAR'),
    ap_data: consolidateAgeing(uaePack?.ap_data, saudiPack?.ap_data, 'SAR'),
    ajc_wip: uaePack?.ajc_wip || saudiPack?.ajc_wip, // show UAE's for now
    avb: uaePack?.avb || saudiPack?.avb, // show UAE's for now
  };
}
function pctClass(p) {
  const x = parseFloat(p) || 0;
  if (x >= 40) return 'good';
  if (x >= 20) return 'warn';
  return 'danger';
}
function varClass(v) { return parseFloat(v) >= 0 ? 'good' : 'danger'; }
function npClass(v) { return parseFloat(v) >= 0 ? 'row-np-pos' : 'row-np-neg'; }

const MIS_TABS = [
  { id: 'exec', label: 'Exec Summary' },
  { id: 'pl', label: 'P&L Consol' },
  { id: 'split', label: null }, // dynamic label based on entity
  { id: 'customers', label: 'Customer Analysis' },
  { id: 'avb', label: 'Actual vs Budget' },
  { id: 'ar', label: 'AR Ageing' },
  { id: 'ap', label: 'AP Ageing' },
  { id: 'bs', label: 'Balance Sheet' },
];

function getTabLabel(tab, entity) {
  if (tab.id === 'split') {
    if (entity === 'saudi') return 'Haziq & Shahalam';
    if (entity === 'group') return 'Entity Breakdown';
    return 'Dubai & Warehouse';
  }
  return tab.label;
}

const FILE_DEFS = [ // eslint-disable-line
  { id: 'mis_ytd', label: 'MIS YTD', desc: 'MIS Jan-May (full year file)', color: '#0e7c7b' },
  { id: 'mis_period', label: 'MIS Period', desc: 'MIS Apr-May (current period)', color: '#0e7c7b' },
  { id: 'gp', label: 'Customer GP', desc: 'Dubai-Customerwise GP report', color: '#c9a84c' },
  { id: 'ar', label: 'AR Aging', desc: 'AR Tally export', color: '#ef4444' },
  { id: 'ap', label: 'AP Aging', desc: 'AP Tally export', color: '#ef4444' },
  { id: 'ajc_wip', label: 'AJC & WIP', desc: 'AJC & WIP monthly file', color: '#3b82f6' },
  { id: 'avb', label: 'Act vs Budget', desc: 'P&L Actual vs Budget', color: '#f97316' },
];

// ─── EXEC SUMMARY ────────────────────────────────────────────────────────────
function ExecSummary({ pack, entity }) {
  const consol = pack.mis_ytd?.Consol;
  if (!consol) return <div className="mis-empty">Upload MIS YTD file to see executive summary.</div>;

  const cur = (v) => fmtCurrency(v, entity);

  const npLine = consol.lines.find(l => l.label.toUpperCase().includes('NET PROFIT'));
  const gpLine = consol.lines.find(l => l.label.toUpperCase().includes('GROSS PROFIT') && !l.label.includes('%'));
  const salesLine = consol.lines.find(l => l.label.toUpperCase() === 'SALES');
  const gpPctLine = consol.lines.find(l => l.label.toUpperCase().includes('GP %'));

  const months = consol.months;
  const liveMonths = months.filter((_, i) => salesLine && salesLine.values[i] > 0);

  const ytdSales = salesLine?.ytd || 0;
  const ytdGP = gpLine?.ytd || 0;
  const ytdNP = npLine?.ytd || 0;
  const ytdGPPct = ytdSales > 0 ? (ytdGP / ytdSales * 100) : 0;
  const ytdNPPct = ytdSales > 0 ? (ytdNP / ytdSales * 100) : 0;

  // Latest month
  const latestIdx = liveMonths.length - 1;
  const latestSales = salesLine?.values[latestIdx] || 0;
  const latestGP = gpLine?.values[latestIdx] || 0;
  const latestNP = npLine?.values[latestIdx] || 0;
  const latestMonth = liveMonths[liveMonths.length - 1] || '';

  return (
    <div className="mis-section">
      <div className="mis-period-badge">Review Period: {latestMonth} 2026 · YTD Jan–{latestMonth}</div>

      <div className="kpi-row">
        <div className="kpi gold">
          <span className="kpi-label">YTD Revenue</span>
          <span className="kpi-value">{cur(ytdSales)}</span>
          <span className="kpi-sub">{liveMonths.length} months</span>
        </div>
        <div className={`kpi ${pctClass(ytdGPPct)}`} style={{borderLeftColor: ytdGPPct >= 20 ? 'var(--green)' : ytdGPPct >= 15 ? 'var(--amber)' : 'var(--red)'}}>
          <span className="kpi-label">YTD Gross Profit</span>
          <span className="kpi-value">{cur(ytdGP)}</span>
          <span className="kpi-sub">GP% {ytdGPPct.toFixed(1)}%</span>
        </div>
        <div className={`kpi ${ytdNP >= 0 ? '' : 'danger'}`} style={{borderLeftColor: ytdNP >= 0 ? 'var(--green)' : 'var(--red)'}}>
          <span className="kpi-label">YTD Net Profit</span>
          <span className="kpi-value">{cur(ytdNP)}</span>
          <span className="kpi-sub">NP% {ytdNPPct.toFixed(1)}%</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">{latestMonth} Revenue</span>
          <span className="kpi-value">{cur(latestSales)}</span>
          <span className="kpi-sub">GP: {cur(latestGP)}</span>
        </div>
        <div className={`kpi ${latestNP >= 0 ? '' : 'warn'}`}>
          <span className="kpi-label">{latestMonth} Net Profit</span>
          <span className="kpi-value" style={{color: latestNP >= 0 ? 'var(--green)' : 'var(--red)'}}>{cur(latestNP)}</span>
          <span className="kpi-sub">NP% {latestSales > 0 ? (latestNP/latestSales*100).toFixed(1) : 0}%</span>
        </div>
      </div>

      {/* Month trend mini-table */}
      <div className="card">
        <div className="card-title">Monthly Trend — Consolidated</div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Metric</th>
                {liveMonths.map(m => <th key={m} className="right">{m}</th>)}
                <th className="right col-ytd">YTD</th>
              </tr>
            </thead>
            <tbody>
              {[salesLine, gpLine, gpPctLine, npLine].filter(Boolean).map((line, i) => {
                const isPct = line.label.includes('%');
                const isNP = line.label.toUpperCase().includes('NET PROFIT');
                const isGP = line.label.toUpperCase().includes('GROSS PROFIT') && !isPct;
                // For YTD GP%, recalculate from summed values rather than using stored ytd
                const ytdPctValue = isPct && line.label.toUpperCase().includes('GP')
                  ? (ytdSales > 0 ? (ytdGP / ytdSales * 100) : 0)
                  : isPct && line.label.toUpperCase().includes('NP')
                  ? (ytdSales > 0 ? (ytdNP / ytdSales * 100) : 0)
                  : line.ytd;
                return (
                  <tr key={i} className={isNP ? npClass(line.ytd) : isGP ? 'row-gp' : ''}>
                    <td>{line.label}</td>
                    {line.values.slice(0, liveMonths.length).map((v, j) => (
                      <td key={j} className={`right ${isPct ? pctClass(v) : ''}`}>
                        {isPct ? `${fmt(v, 1)}%` : cur(v)}
                      </td>
                    ))}
                    <td className={`right col-ytd ${isPct ? pctClass(ytdPctValue) : ''}`}>
                      {isPct ? `${fmt(ytdPctValue, 1)}%` : cur(line.ytd)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* AJC/WIP summary */}
      {pack.ajc_wip && (
        <div className="kpi-row" style={{marginTop: 12}}>
          <div className="kpi warn">
            <span className="kpi-label">AJC (Accrued Jobs)</span>
            <span className="kpi-value">{cur(pack.ajc_wip.ajcTotal)}</span>
            <span className="kpi-sub">{pack.ajc_wip.ajc.length} jobs</span>
          </div>
          <div className="kpi">
            <span className="kpi-label">WIP</span>
            <span className="kpi-value">{cur(pack.ajc_wip.wipTotal)}</span>
            <span className="kpi-sub">{pack.ajc_wip.wip.length} jobs</span>
          </div>
          <div className="kpi gold">
            <span className="kpi-label">Revenue WIP</span>
            <span className="kpi-value">{cur(pack.ajc_wip.revWipTotal)}</span>
            <span className="kpi-sub">Not yet invoiced</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── P&L CONSOL ──────────────────────────────────────────────────────────────
function PLConsol({ pack, entity }) {
  const [view, setView] = useState('ytd'); // ytd or period
  const mis = view === 'ytd' ? pack.mis_ytd : pack.mis_period;
  const consol = mis?.Consol;

  if (!consol) return <div className="mis-empty">Upload MIS {view === 'ytd' ? 'YTD' : 'Period'} file.</div>;

  const cur = (v) => fmtCurrency(v, entity);

  const months = consol.months;
  const liveMonths = months.filter((_, i) => {
    const salesLine = consol.lines.find(l => l.label === 'Sales');
    return salesLine && salesLine.values[i] > 0;
  });

  return (
    <div className="mis-section">
      <div className="toggle-group">
        <button className={`toggle-btn ${view === 'ytd' ? 'active' : ''}`} onClick={() => setView('ytd')}>YTD</button>
        <button className={`toggle-btn ${view === 'period' ? 'active' : ''}`} onClick={() => setView('period')}>Current Period</button>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{minWidth: 220}}>Particulars</th>
              {liveMonths.map(m => <th key={m} className="right">{m}</th>)}
              <th className="right col-ytd">YTD</th>
            </tr>
          </thead>
          <tbody>
            {consol.lines.map((line, i) => {
              const isPct = line.label.includes('%');
              const isNP = line.label.toUpperCase().includes('NET PROFIT');
              const isGP = line.label.toUpperCase().includes('GROSS PROFIT') && !isPct;
              const isHeader = isNP || isGP;
              const isSubRow = !isHeader && !['Sales', 'Less: Purchases'].includes(line.label);

              // Skip zero rows
              if (!isHeader && line.ytd === 0 && line.values.every(v => v === 0)) return null;

              // For YTD %, recalculate from actual totals instead of averaging monthly %s
              let ytdPct = line.ytd;
              if (isPct) {
                const salesLine = consol.lines.find(l => l.label.toUpperCase() === 'SALES');
                const isGPPct = line.label.toUpperCase().includes('GP');
                const isNPPct = line.label.toUpperCase().includes('NP') || line.label.toUpperCase().includes('NET');
                if (isGPPct && salesLine?.ytd > 0) {
                  const gpLine = consol.lines.find(l => l.label.toUpperCase().includes('GROSS PROFIT') && !l.label.includes('%'));
                  ytdPct = gpLine ? (gpLine.ytd / salesLine.ytd * 100) : line.ytd;
                } else if (isNPPct && salesLine?.ytd > 0) {
                  const npLine = consol.lines.find(l => l.label.toUpperCase().includes('NET PROFIT') && !l.label.includes('%'));
                  ytdPct = npLine ? (npLine.ytd / salesLine.ytd * 100) : line.ytd;
                }
              }

              return (
                <tr key={i} className={isNP ? npClass(line.ytd) : isGP ? 'row-gp' : ''}>
                  <td style={isSubRow ? { paddingLeft: 20, color: '#64748b' } : { fontWeight: isHeader ? 700 : 500 }}>
                    {line.label}
                  </td>
                  {line.values.slice(0, liveMonths.length).map((v, j) => (
                    <td key={j} className={`right ${isPct ? pctClass(v) : ''}`}>
                      {isPct ? `${fmt(v, 1)}%` : cur(v)}
                    </td>
                  ))}
                  <td className={`right col-ytd bold ${isPct ? pctClass(ytdPct) : ''}`}>
                    {isPct ? `${fmt(ytdPct, 1)}%` : cur(line.ytd)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── DUBAI VS WAREHOUSE / HAZIQ & SHAHALAM ───────────────────────────────────
function DubaiVsWarehouse({ pack, entity }) {
  const mis = pack.mis_ytd;
  if (!mis?.Dubai || !mis?.Warehouse) return <div className="mis-empty">Upload MIS YTD file.</div>;

  const cur = (v) => fmtCurrency(v, entity);
  const e1 = entity === 'saudi' ? 'Haziq' : 'Dubai';
  const e2 = entity === 'saudi' ? 'Shahalam' : 'Warehouse';

  const dSales = mis.Dubai.lines.find(l => l.label === 'Sales');
  const wSales = mis.Warehouse.lines.find(l => l.label === 'Sales');
  const dGP = mis.Dubai.lines.find(l => l.label.toUpperCase().includes('GROSS PROFIT') && !l.label.includes('%'));
  const wGP = mis.Warehouse.lines.find(l => l.label.toUpperCase().includes('GROSS PROFIT') && !l.label.includes('%'));
  const dNP = mis.Dubai.lines.find(l => l.label.toUpperCase().includes('NET PROFIT'));
  const wNP = mis.Warehouse.lines.find(l => l.label.toUpperCase().includes('NET PROFIT'));

  const months = mis.Dubai.months;
  const liveMonths = months.filter((_, i) => dSales && dSales.values[i] > 0);

  const rows = [
    { label: `${e1} Sales`, dLine: dSales, wLine: null },
    { label: `${e2} Sales`, dLine: null, wLine: wSales },
    { label: `${e1} GP`, dLine: dGP, wLine: null },
    { label: `${e2} GP`, dLine: null, wLine: wGP },
    { label: `${e1} Net Profit`, dLine: dNP, wLine: null, isD: true },
    { label: `${e2} Net Profit`, dLine: null, wLine: wNP, isW: true },
  ];

  return (
    <div className="mis-section">
      <div className="kpi-row">
        {[
          { label: `${e1} YTD Sales`, val: dSales?.ytd, sub: `GP: ${dGP?.ytd > 0 ? (dGP.ytd/dSales.ytd*100).toFixed(1) : 0}%` },
          { label: `${e1} YTD Net`, val: dNP?.ytd, isNP: true },
          { label: `${e2} YTD Sales`, val: wSales?.ytd, sub: `GP: ${wGP?.ytd > 0 ? (wGP.ytd/wSales.ytd*100).toFixed(1) : 0}%` },
          { label: `${e2} YTD Net`, val: wNP?.ytd, isNP: true },
        ].map((k, i) => (
          <div key={i} className={`kpi ${k.isNP ? (parseFloat(k.val) >= 0 ? '' : 'danger') : ''}`}
            style={{ borderLeftColor: k.isNP ? (parseFloat(k.val) >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--teal)' }}>
            <span className="kpi-label">{k.label}</span>
            <span className="kpi-value" style={{ color: k.isNP && k.val < 0 ? 'var(--red)' : undefined }}>
              {cur(k.val)}
            </span>
            {k.sub && <span className="kpi-sub">{k.sub}</span>}
          </div>
        ))}
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Entity / Metric</th>
              {liveMonths.map(m => <th key={m} className="right">{m}</th>)}
              <th className="right col-ytd">YTD</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const line = row.dLine || row.wLine;
              if (!line) return null;
              const isNP = row.label.includes('Net');
              return (
                <tr key={i} className={isNP ? npClass(line.ytd) : ''}>
                  <td style={{ fontWeight: isNP ? 700 : 500 }}>{row.label}</td>
                  {line.values.slice(0, liveMonths.length).map((v, j) => (
                    <td key={j} className={`right ${isNP && v < 0 ? 'danger' : isNP && v > 0 ? 'good' : ''}`}>
                      {cur(v)}
                    </td>
                  ))}
                  <td className={`right col-ytd bold ${isNP && line.ytd < 0 ? 'danger' : isNP && line.ytd > 0 ? 'good' : ''}`}>
                    {cur(line.ytd)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── CUSTOMER ANALYSIS ───────────────────────────────────────────────────────
function CustomerAnalysis({ pack, entity }) {
  const [view, setView] = useState('freight');
  const gp = pack.gp;
  if (!gp) return <div className="mis-empty">Upload Customer GP report.</div>;

  const cur = (v) => fmtCurrency(v, entity);
  const customers = view === 'freight' ? gp.freight : gp.warehouse;
  const sorted = [...customers].sort((a, b) => b.ytdGP - a.ytdGP);
  const totalSales = sorted.reduce((s, c) => s + c.ytdSales, 0);
  const totalGP = sorted.reduce((s, c) => s + c.ytdGP, 0);
  // Only show months where actual data exists — not future empty months
  const liveMonthSet = new Set();
  (gp.freight || []).concat(gp.warehouse || []).forEach(c => {
    (c.monthly || []).forEach(mo => { if (mo.sales > 0 || mo.gp > 0) liveMonthSet.add(mo.month); });
  });
  const months = gp.months.filter(m => m && !m.includes('YTD') && liveMonthSet.has(m));

  const freightLabel = entity === 'saudi' ? 'Haziq' : 'Freight';
  const warehouseLabel = entity === 'saudi' ? 'Shahalam' : 'Warehouse';

  return (
    <div className="mis-section">
      <div className="toggle-group">
        <button className={`toggle-btn ${view === 'freight' ? 'active' : ''}`} onClick={() => setView('freight')}>
          {freightLabel} ({gp.freight.length} clients)
        </button>
        <button className={`toggle-btn ${view === 'warehouse' ? 'active' : ''}`} onClick={() => setView('warehouse')}>
          {warehouseLabel} ({gp.warehouse.length} clients)
        </button>
      </div>

      <div className="kpi-row">
        <div className="kpi gold">
          <span className="kpi-label">YTD Revenue</span>
          <span className="kpi-value">{cur(totalSales)}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">YTD GP</span>
          <span className="kpi-value">{cur(totalGP)}</span>
          <span className="kpi-sub">Avg GP% {totalSales > 0 ? (totalGP/totalSales*100).toFixed(1) : 0}%</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Active Clients</span>
          <span className="kpi-value">{sorted.filter(c => c.ytdSales > 0).length}</span>
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{minWidth: 200}}>Customer</th>
              <th className="right">YTD Sales</th>
              <th className="right">YTD GP</th>
              <th className="right">GP%</th>
              <th className="right">% of Total GP</th>
              {months.map(m => <th key={m} className="right">{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {sorted.filter(c => c.ytdSales > 0).map((c, i) => (
              <tr key={i}>
                <td className="client-cell">{c.name}</td>
                <td className="right">{cur(c.ytdSales)}</td>
                <td className={`right ${c.ytdGP >= 0 ? 'good' : 'danger'}`}>{cur(c.ytdGP)}</td>
                <td className={`right ${pctClass(c.gpPct)}`}>{fmt(c.gpPct, 1)}%</td>
                <td className="right">{totalGP > 0 ? fmt(c.ytdGP / totalGP * 100, 1) : 0}%</td>
                {months.map((m, j) => {
                  const month = c.monthly.find(mo => mo.month === m);
                  return <td key={j} className="right">{month && month.sales > 0 ? cur(month.sales) : '—'}</td>;
                })}
              </tr>
            ))}
            <tr className="row-ytd">
              <td><strong>TOTAL</strong></td>
              <td className="right bold">{cur(totalSales)}</td>
              <td className="right bold">{cur(totalGP)}</td>
              <td className="right bold">{totalSales > 0 ? fmt(totalGP/totalSales*100, 1) : 0}%</td>
              <td className="right bold">100%</td>
              {months.map((_, j) => <td key={j} />)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── ACT VS BUDGET ───────────────────────────────────────────────────────────
function ActVsBudget({ pack, entity }) {
  const avb = pack.avb;
  if (!avb) return <div className="mis-empty">Upload Actual vs Budget file.</div>;

  const cur = (v) => fmtCurrency(v, entity);

  return (
    <div className="mis-section">
      <div className="mis-period-badge">Period: {avb.period}</div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{minWidth: 220}}>Particulars</th>
              <th className="right">Actual</th>
              <th className="right">Budget</th>
              <th className="right">Variance</th>
              <th className="right">Var %</th>
            </tr>
          </thead>
          <tbody>
            {avb.lines.map((line, i) => {
              if (!line.label) return null;
              if (line.actual === 0 && line.budget === 0) return null;
              const varPct = line.budget !== 0 ? (line.variance / Math.abs(line.budget) * 100) : 0;
              const isHeader = line.isHeader;
              return (
                <tr key={i} className={isHeader ? (line.actual >= 0 ? 'row-gp' : 'row-np-neg') : ''}>
                  <td style={{ fontWeight: isHeader ? 700 : 400 }}>{line.label}</td>
                  <td className="right">{cur(line.actual)}</td>
                  <td className="right">{cur(line.budget)}</td>
                  <td className={`right ${varClass(line.variance)}`}>{cur(line.variance)}</td>
                  <td className={`right ${varClass(line.variance)}`}>{fmt(varPct, 1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── AR AGEING ───────────────────────────────────────────────────────────────
function ARAgeing({ pack, entity }) {
  const ar = pack.ar_data;
  if (!ar || !ar.length) return <div className="mis-empty">AR Ageing data not loaded. Include the AR Tally file in the monthly pack upload.</div>;

  const cur = (v) => fmtCurrency(v, entity);
  const total = ar.reduce((s, r) => s + (r.pending || 0), 0);
  const overdue = ar.reduce((s, r) => s + (r.d60_90 || 0) + (r.d90_180 || 0) + (r.d180_365 || 0) + Math.max(0, r.gt365 || 0), 0);

  return (
    <div className="mis-section">
      <div className="kpi-row">
        <div className="kpi gold"><span className="kpi-label">Total AR</span><span className="kpi-value">{cur(total)}</span></div>
        <div className="kpi warn"><span className="kpi-label">Overdue 60+</span><span className="kpi-value">{cur(overdue)}</span><span className="kpi-sub">{total > 0 ? fmt(overdue/total*100, 1) : 0}% of total</span></div>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th className="right">Total</th>
              <th className="right">&lt;60d</th>
              <th className="right">60-90d</th>
              <th className="right">90-180d</th>
              <th className="right">180-365d</th>
              <th className="right">&gt;365d</th>
            </tr>
          </thead>
          <tbody>
            {ar.filter(r => r.pending > 0).sort((a, b) => b.pending - a.pending).map((r, i) => (
              <tr key={i} className={r.gt365 > 0 || r.d180_365 > 0 ? 'row-danger' : r.d90_180 > 0 ? 'row-warn' : ''}>
                <td className="client-cell">{r.client}</td>
                <td className="right bold">{cur(r.pending)}</td>
                <td className="right good">{r.lt60 > 0 ? cur(r.lt60) : '—'}</td>
                <td className={`right ${r.d60_90 > 0 ? 'warn' : ''}`}>{r.d60_90 > 0 ? cur(r.d60_90) : '—'}</td>
                <td className={`right ${r.d90_180 > 0 ? 'danger' : ''}`}>{r.d90_180 > 0 ? cur(r.d90_180) : '—'}</td>
                <td className={`right ${r.d180_365 > 0 ? 'danger' : ''}`}>{r.d180_365 > 0 ? cur(r.d180_365) : '—'}</td>
                <td className={`right ${r.gt365 > 0 ? 'danger' : ''}`}>{r.gt365 > 0 ? cur(r.gt365) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── AP AGEING ───────────────────────────────────────────────────────────────
function APAgeing({ pack, entity }) {
  const ap = pack.ap_data;
  if (!ap || !ap.length) return <div className="mis-empty">AP Ageing data not loaded. Include the AP Tally file in the monthly pack upload.</div>;

  const cur = (v) => fmtCurrency(v, entity);
  const total = ap.reduce((s, r) => s + (r.pending || 0), 0);

  return (
    <div className="mis-section">
      <div className="kpi-row">
        <div className="kpi warn"><span className="kpi-label">Total AP</span><span className="kpi-value">{cur(total)}</span></div>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Supplier</th>
              <th className="right">Total</th>
              <th className="right">&lt;60d</th>
              <th className="right">60-90d</th>
              <th className="right">90-180d</th>
              <th className="right">180-365d</th>
              <th className="right">&gt;365d</th>
            </tr>
          </thead>
          <tbody>
            {ap.filter(r => r.pending > 0).sort((a, b) => b.pending - a.pending).map((r, i) => (
              <tr key={i}>
                <td className="client-cell">{r.client}</td>
                <td className="right bold">{cur(r.pending)}</td>
                <td className="right">{r.lt60 > 0 ? cur(r.lt60) : '—'}</td>
                <td className={`right ${r.d60_90 > 0 ? 'warn' : ''}`}>{r.d60_90 > 0 ? cur(r.d60_90) : '—'}</td>
                <td className={`right ${r.d90_180 > 0 ? 'warn' : ''}`}>{r.d90_180 > 0 ? cur(r.d90_180) : '—'}</td>
                <td className={`right ${r.d180_365 > 0 ? 'danger' : ''}`}>{r.d180_365 > 0 ? cur(r.d180_365) : '—'}</td>
                <td className={`right ${r.gt365 > 0 ? 'danger' : ''}`}>{r.gt365 > 0 ? cur(r.gt365) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── BALANCE SHEET ────────────────────────────────────────────────────────────
function BalanceSheet({ bsData }) {
  if (!bsData) return <div className="mis-empty">Balance sheet data not available. (PDF format — upload via monthly pack)</div>;

  const { assets, liabilities, period } = bsData;

  return (
    <div className="mis-section">
      <div className="mis-period-badge">{period}</div>
      <div className="grid-2">
        <div className="card">
          <div className="card-title" style={{color: 'var(--teal)'}}>Assets</div>
          {assets?.map((item, i) => (
            <div key={i} className="bs-row" style={{ paddingLeft: item.indent ? 16 : 0 }}>
              <span style={{ fontWeight: item.isHeader ? 700 : 400, color: item.isHeader ? 'var(--navy)' : undefined }}>{item.label}</span>
              <span style={{ fontWeight: item.isHeader ? 700 : 400 }}>{item.value ? fmtAED(item.value) : ''}</span>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="card-title" style={{color: 'var(--red)'}}>Liabilities</div>
          {liabilities?.map((item, i) => (
            <div key={i} className="bs-row" style={{ paddingLeft: item.indent ? 16 : 0 }}>
              <span style={{ fontWeight: item.isHeader ? 700 : 400, color: item.isHeader ? 'var(--navy)' : undefined }}>{item.label}</span>
              <span style={{ fontWeight: item.isHeader ? 700 : 400 }}>{item.value ? fmtAED(item.value) : ''}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── FILE UPLOAD FOR MONTHLY PACK ────────────────────────────────────────────
function MonthlyFileUpload({ onPack, existingPack }) {
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const detectType = (filename) => {
    const n = filename.toUpperCase();
    if (n.endsWith('.PDF')) return null; // never route PDFs to any MIS slot
    if (n.includes('CUSTOMER') || n.includes('GP') || n.includes('CUSTOMERWISE')) return 'gp';
    if (n.includes('APT') || n.includes('AP_') || n.includes('AP ') || (n.includes('AP') && n.includes('TALLY'))) return 'ap';
    if (n.includes('AR') || n.includes('DEBTOR')) return 'ar';
    if (n.includes('AJC') || n.includes('WIP')) return 'ajc_wip';
    if (n.includes('BUDGET') || n.includes('AVB') || n.includes('ACT')) return 'avb';
    if (n.includes('APR') || n.includes('PERIOD') || (n.includes('MIS') && (n.includes('APR') || n.includes('APRIL')))) return 'mis_period';
    if (n.includes('MIS') || n.includes('JAN')) return 'mis_ytd';
    return null;
  };

  const handleFiles = (fileList) => {
    Array.from(fileList).forEach(file => {
      const type = detectType(file.name);
      if (!type) return;
      const reader = new FileReader();
      reader.onload = e => {
        setFiles(prev => ({ ...prev, [type]: { buffer: new Uint8Array(e.target.result), name: file.name } }));
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const process = () => {
    setLoading(true);
    try {
      const buffers = {};
      Object.keys(files).forEach(k => { buffers[k] = files[k].buffer; });
      const pack = assembleMonthlyPack(buffers);
      onPack(pack);
    } catch(e) {
      console.error(e);
    }
    setLoading(false);
  };

  const loadedCount = Object.keys(files).length;
  const FILE_LABELS = { mis_ytd: 'MIS YTD', mis_period: 'MIS Period', gp: 'Customer GP', ar: 'AR Aging', ap: 'AP Aging', ajc_wip: 'AJC & WIP', avb: 'Act vs Budget' };

  return (
    <div className="monthly-upload">
      <h3 className="monthly-upload-title">Upload Monthly Pack</h3>
      <p className="monthly-upload-sub">Drop all files from your CFO's monthly email at once — auto-detected by filename.</p>

      <div
        className={`drop-zone large ${dragging ? 'dragging' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.xlsx,.xls,.pdf';
          input.multiple = true;
          input.onchange = e => handleFiles(e.target.files);
          input.click();
        }}
      >
        <span className="drop-icon">📂</span>
        <span>{dragging ? 'Drop files here' : 'Drop all monthly files here, or click to select'}</span>
        <span style={{fontSize: 11, color: 'var(--text-light)', marginTop: 4}}>MIS YTD · MIS Period · Customer GP · AR · AP · AJC/WIP · Act vs Budget</span>
      </div>

      {loadedCount > 0 && (
        <div className="monthly-loaded-files">
          {Object.entries(files).map(([type, f]) => (
            <span key={type} className="loaded-file-tag">✓ {FILE_LABELS[type] || type}: {f.name}</span>
          ))}
        </div>
      )}

      {loadedCount > 0 && (
        <button className="btn-primary" style={{marginTop: 12}} onClick={process} disabled={loading}>
          {loading ? 'Processing...' : `Process ${loadedCount} file${loadedCount !== 1 ? 's' : ''}`}
        </button>
      )}
      {existingPack && (
        <p style={{fontSize: 11, color: 'var(--text-light)', marginTop: 8}}>✓ Monthly data loaded — upload new files to refresh</p>
      )}
    </div>
  );
}

// ─── MAIN MONTHLY MIS VIEW ───────────────────────────────────────────────────
export default function MonthlyMIS({ entity }) {
  const [misTab, setMisTab] = useState('exec');
  const [pack, setPack] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [misLoading, setMisLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);

  // Load from Supabase when entity changes
  useEffect(() => {
    setMisLoading(true);
    setMisTab('exec');

    if (entity === 'group') {
      Promise.all([loadMISData('uae'), loadMISData('saudi')]).then(([uaePack, saudiPack]) => {
        if (uaePack || saudiPack) {
          setPack({ _group: true, uae: uaePack, saudi: saudiPack });
        } else {
          setPack(null);
        }
        setShowUpload(false);
        setMisLoading(false);
      }).catch(() => {
        setPack(null);
        setMisLoading(false);
      });
    } else {
      loadMISData(entity).then(entityPack => {
        setPack(entityPack);
        setShowUpload(!entityPack);
        setMisLoading(false);
      }).catch(() => {
        setPack(null);
        setShowUpload(true);
        setMisLoading(false);
      });
    }
  }, [entity]);

  const handlePack = useCallback((newPack) => {
    setSaveStatus('saving');
    saveMISData(entity, newPack)
      .then(() => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 3000);
      })
      .catch(() => {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 4000);
      });
    setPack(newPack);
    setShowUpload(false);
  }, [entity]);

  // For group view, consolidate UAE + Saudi into USD
  const activePack = pack?._group
    ? buildGroupPack(pack.uae, pack.saudi)
    : pack;

  if (misLoading) {
    return (
      <div className="view monthly-mis" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <span style={{ color: '#6b7280', fontSize: 14 }}>Loading MIS data…</span>
      </div>
    );
  }

  return (
    <div className="view monthly-mis">
      <div className="mis-header">
        <div className="mis-header-left">
          <h2 className="mis-title">Monthly MIS</h2>
          <span className="mis-entity-badge">
            {entity === 'uae' ? '🇦🇪 UAE' : entity === 'saudi' ? '🇸🇦 Saudi' : '🌐 Group'}
          </span>
          {activePack && <span className="mis-loaded-badge">✓ Data loaded</span>}
          {saveStatus === 'saving' && <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>Saving…</span>}
          {saveStatus === 'saved' && <span style={{ color: '#10b981', fontSize: 12, marginLeft: 8 }}>✓ Saved</span>}
          {saveStatus === 'error' && <span style={{ color: '#ef4444', fontSize: 12, marginLeft: 8 }}>⚠ Save failed</span>}
        </div>
        {entity !== 'group' && (
        <button className="btn-primary" onClick={() => setShowUpload(!showUpload)}>
          {showUpload ? 'Hide Upload' : '↑ Upload New Month'}
        </button>
        )}
      </div>

      {showUpload && entity !== 'group' && (
        <MonthlyFileUpload onPack={handlePack} existingPack={activePack} />
      )}

      {entity === 'group' && !activePack && (
        <div className="mis-empty" style={{padding: 40, textAlign: 'center'}}>
          Upload UAE and/or Saudi monthly packs first. Group view combines both automatically.
        </div>
      )}

      {activePack && (
        <>
          {/* Sub-tab nav */}
          <nav className="mis-tab-nav">
            {MIS_TABS.map(t => (
              <button
                key={t.id}
                className={`mis-tab-btn ${misTab === t.id ? 'active' : ''}`}
                onClick={() => setMisTab(t.id)}
              >
                {getTabLabel(t, entity)}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="mis-content">
            {misTab === 'exec' && <ExecSummary pack={activePack} entity={entity} />}
            {misTab === 'pl' && <PLConsol pack={activePack} entity={entity} />}
            {misTab === 'split' && <DubaiVsWarehouse pack={activePack} entity={entity} />}
            {misTab === 'customers' && <CustomerAnalysis pack={activePack} entity={entity} />}
            {misTab === 'avb' && <ActVsBudget pack={activePack} entity={entity} />}
            {misTab === 'ar' && <ARAgeing pack={activePack} entity={entity} />}
            {misTab === 'ap' && <APAgeing pack={activePack} entity={entity} />}
            {misTab === 'bs' && <BalanceSheet bsData={null} entity={entity} />}
          </div>
        </>
      )}

      {!activePack && !showUpload && entity !== 'group' && (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h2>No monthly data loaded</h2>
          <button className="btn-primary" onClick={() => setShowUpload(true)}>Upload Monthly Pack</button>
        </div>
      )}
    </div>
  );
}
